import useOptimisticAddNewCustomerAddress from '@/api/mutations/customers/optimistic/useOptimisticAddNewCustomerAddress';
import useOptimisticDeleteCustomerAddress from '@/api/mutations/customers/optimistic/useOptimisticDeleteCustomerAddress';
import useOptimisticSetCustomerBillingAddress from '@/api/mutations/customers/optimistic/useOptimisticSetCustomerBillingAddress';
import useOptimisticUpdateCustomerAddress from '@/api/mutations/customers/optimistic/useOptimisticUpdateCustomerAddress';
import useOptimisticUpdateCustomerContactOnCustomer from '@/api/mutations/customers/optimistic/useOptimisticUpdateCustomerContactOnCustomer';
import { Address } from '@/gql/graphql';
import AddEditContactModal, {
  IProps as IAddEditContactModalProps,
} from '@/ui/components/addEditContactControl/AddEditContactModal';
import AddAddressModal, {
  IProps as IAddAddressModalProps,
} from '@/ui/components/addressModals/AddAddressModal';
import SelectEditAddressAndAddressContactsModal, {
  IProps as ISelectEditAddressAndAddressContactsModalProps,
} from '@/ui/components/addressModals/SelectEditAddressAndAddressContactsModal';
import {
  AnchorButton,
  Button,
  InputIcon,
  ManageableItemsList,
  Modal,
  TTableRowAction,
} from '@payaca/ambrosia';
import AddressComponent from '@payaca/components/address/Address';
import GoogleMapAnchor from '@payaca/components/googleMapAnchor/GoogleMapAnchor';

import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useMemo } from 'react';
import { Link, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import useGetCustomer from '../../../../api/queries/customers/useGetCustomer';
import useGetCustomerAddresses from '../../../../api/queries/customers/useGetCustomerAddresses';
import { useAddressTableState } from './useAddressTableState';

type TableData = Pick<
  Address,
  | 'id'
  | 'line1'
  | 'line2'
  | 'city'
  | 'administrativeArea'
  | 'country'
  | 'postalCode'
  | 'fullLocalAddress'
  | 'coordinates'
  | 'contacts'
>;

const PAGE_SIZE = 20;

const CustomerAddressTable: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();

  const {
    searchQuery,
    debouncedSearchQuery,
    currentPage,
    setSearchQuery,
    setCurrentPage,
  } = useAddressTableState();

  /**
   * React router
   */
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const isAddingAddress = useRouteMatch({
    path: `${path}/new`,
    exact: true,
  });
  const isEditingAddress = useRouteMatch<{
    addressId: string;
  }>({
    path: `${path}/:addressId/edit`,
    exact: true,
  });
  const isDeletingAddress = useRouteMatch<{
    addressId: string;
  }>({
    path: `${path}/:addressId/delete`,
    exact: true,
  });
  const isEditingAddressAccessContact = useRouteMatch<{
    addressId: string;
    contactId: string;
  }>({
    path: `${path}/:addressId/contact/:contactId/edit`,
    exact: true,
  });

  /**
   * Queries
   */
  // Keep useGetCustomer for billing address check
  const { customer } = useGetCustomer(customerId);

  // Use paginated query for addresses
  const limit = PAGE_SIZE;
  const offset = (currentPage - 1) * PAGE_SIZE;
  const {
    addresses: paginatedAddresses,
    isFetching,
    isLoading,
  } = useGetCustomerAddresses(
    customerId,
    { searchTerm: debouncedSearchQuery || undefined },
    { limit, offset }
  );

  const addresses = useMemo(
    () => paginatedAddresses?.items || [],
    [paginatedAddresses?.items]
  );
  const totalCount = paginatedAddresses?.totalCount || 0;

  /**
   * Mutations
   */
  const { mutate: createNewCustomerAddress } =
    useOptimisticAddNewCustomerAddress(customerId);
  const { mutate: updateAddress } =
    useOptimisticUpdateCustomerAddress(customerId);
  const { mutate: deleteAddress } =
    useOptimisticDeleteCustomerAddress(customerId);
  const { mutate: setBillingAddress } =
    useOptimisticSetCustomerBillingAddress(customerId);
  const { mutate: updateCustomerContact } =
    useOptimisticUpdateCustomerContactOnCustomer(customerId);

  /**
   * Callbacks
   */
  const handleCreateAddress = useCallback<
    Required<IAddAddressModalProps>['onSave']
  >(
    // oxlint-disable-next-line typescript/no-misused-promises -- FIXME: pre-existing error: Promise returned in function argument where a void return was expected.
    async (address) => {
      createNewCustomerAddress({
        address: {
          ...address,
        },
        customerId,
      });

      history.push(url);
    },
    [createNewCustomerAddress, customerId, history, url]
  );
  const handleUpdateAddress = useCallback<
    Required<ISelectEditAddressAndAddressContactsModalProps>['onChange']
  >(
    // oxlint-disable-next-line typescript/no-misused-promises -- FIXME: pre-existing error: Promise returned in function argument where a void return was expected.
    async (address) => {
      const _address = { ...address };

      delete _address.fullLocalAddress;

      updateAddress({
        ..._address,
      });
    },
    [updateAddress]
  );
  const handleDeleteAddress = useCallback(() => {
    if (!isDeletingAddress) {
      // should never get here
      return;
    }

    deleteAddress({
      addressId: isDeletingAddress?.params.addressId,
      customerId,
    });

    history.push(url);
  }, [isDeletingAddress, history, deleteAddress, url, customerId]);
  const handleEditAddressAccessContact = useCallback<
    Required<IAddEditContactModalProps>['onSave']
  >(
    (contact) => {
      if (!isEditingAddressAccessContact) {
        return;
      }

      const { id, ...rest } = contact;

      const address = addresses.find(
        (address) =>
          address.id === isEditingAddressAccessContact.params.addressId
      );

      if (id && address) {
        const _address = { ...address };
        delete _address.fullLocalAddress;

        updateAddress({
          ..._address,
          contacts: _address.contacts.map((contact) =>
            contact.id === id ? { ...contact, ...rest } : contact
          ),
        });
      }

      history.push(url);
    },
    [addresses, updateAddress, history, url, isEditingAddressAccessContact]
  );

  const rowActions = useMemo<(row: TableData) => TTableRowAction<TableData>[]>(
    () => (row) => {
      const actions: TTableRowAction<TableData>[] = [
        {
          label: 'Edit',
          onClick: (row) => history.push(`${url}/${row.id}/edit`),
        },
        {
          label: 'Delete',
          onClick: (row) => history.push(`${url}/${row.id}/delete`),
        },
      ];

      if (customer?.billingAddress?.id !== row.id) {
        actions.splice(1, 0, {
          label: 'Make billing address',
          onClick: () => setBillingAddress({ addressId: row.id, customerId }),
        });
      }

      return actions;
    },
    [history, url, customer, setBillingAddress, customerId]
  );

  const addressBeingEdited = useMemo(() => {
    if (isEditingAddress) {
      return addresses.find(
        (address) => address.id === isEditingAddress.params.addressId
      );
    }
  }, [isEditingAddress, addresses]);

  return (
    <div className="@container">
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Customer addresses"
          buttons={
            <Button
              className="md:whitespace-nowrap"
              size={'sm'}
              onClick={() => history.push(`${url}/new`)}
            >
              Add address
            </Button>
          }
        />

        <ManageableItemsList.ActionBar>
          <ManageableItemsList.ActionBar.SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            leadingElement={
              isFetching ? (
                <UntitledIcon
                  name="loading-02.3"
                  className="h-4 w-4 animate-spin text-gray-400"
                />
              ) : undefined
            }
          />
        </ManageableItemsList.ActionBar>

        <ManageableItemsList.Table<TableData>
          uniqueKey={'id'}
          items={addresses}
          itemActions={rowActions}
          isLoading={isLoading}
        >
          <ManageableItemsList.Table.Column<TableData, 'id'>
            field="id"
            header="Address"
            render={(field, row) => {
              return (
                <GoogleMapAnchor
                  addressText={row.fullLocalAddress}
                  coordinates={row.coordinates}
                >
                  <AddressComponent
                    address={{
                      line1: row.line1,
                      line2: row.line2,
                      city: row.city,
                      administrativeArea: row.administrativeArea,
                      postcode: row.postalCode,
                    }}
                  />
                </GoogleMapAnchor>
              );
            }}
          />

          <ManageableItemsList.Table.Column<TableData, 'contacts'>
            field="contacts"
            header="Access contacts"
            render={(contacts, row) => {
              return (
                <div className="flex flex-wrap gap-3">
                  {contacts.map((contact) => (
                    <AnchorButton
                      as={Link}
                      key={contact.id}
                      to={`${url}/${row.id}/contact/${contact.id}/edit`}
                      variant="secondary"
                      size={'xs'}
                    >
                      {contact.name}
                    </AnchorButton>
                  ))}
                </div>
              );
            }}
          />

          <ManageableItemsList.Table.Column<TableData, 'id'>
            field="id"
            header="Billing address"
            render={(id, row) => {
              if (customer?.billingAddress?.id === id) {
                return <UntitledIcon className="h-6 w-6" name="check" />;
              }
            }}
          />
        </ManageableItemsList.Table>

        <ManageableItemsList.PaginationBar
          type="from-paginated-result"
          limit={limit}
          offset={offset}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
        />
      </ManageableItemsList>
      <AddEditContactModal
        isOpen={!!isEditingAddressAccessContact}
        title="Edit access contact"
        contact={
          isEditingAddressAccessContact
            ? addresses
                .find(
                  (address) =>
                    address.id ===
                    isEditingAddressAccessContact.params.addressId
                )
                ?.contacts.find(
                  (contact) =>
                    contact.id ===
                    isEditingAddressAccessContact.params.contactId
                )
            : undefined
        }
        onClose={() => history.push(url)}
        onSave={handleEditAddressAccessContact}
      />
      <AddAddressModal
        isOpen={!!isAddingAddress}
        onClose={() => history.push(url)}
        onSave={handleCreateAddress}
      />
      <SelectEditAddressAndAddressContactsModal
        isOpen={!!isEditingAddress}
        onClose={() => history.push(url)}
        onDone={() => history.push(url)}
        selectedAddress={addressBeingEdited}
        disableAddressChange
        onChange={handleUpdateAddress}
        customerId={customerId}
      />
      <Modal
        title="Delete address"
        isOpen={!!isDeletingAddress}
        onClose={() => history.push(url)}
      >
        <Modal.Body>
          <p>Are you sure you want to delete this address?</p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" onClick={() => history.push(url)}>
              Cancel
            </Button>
            <Button colour="negative" onClick={handleDeleteAddress}>
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerAddressTable;
